import {
  BookUser,
  Home, ClipboardCheck, CalendarCheck, Briefcase, ListChecks, MessageSquare,
  Users, Shield, IdCard, Camera, Hash, Calendar, MapPin, Building2, Map,
  Settings, FileInput, Upload, Layers, Coins, SlidersHorizontal, Scale,
  Stethoscope, BarChart3, FileText, Download, Package, Wallet, RefreshCw,
  Mail, ShieldAlert, ShieldCheck, FileBarChart2, FileArchive, Tablet, LucideIcon,
  Clock, History, Thermometer, MapPinCheck,
} from 'lucide-react';
import { Page } from '../../interfaces/PagesInterface';
import { AdminEmployeeRole } from '../../interfaces/EmployeeInfoInterface';
import { MiscTaskApprovalConfig } from '../../interfaces/LayoutInterface';
import { SiteConfig } from '../../interfaces/ConfigInterface';
import { can, canViewWorkOrders } from '../../util/adminPermissions';

/**
 * Data-driven sidebar menu for the v2 shell. Every item mirrors a link rendered by
 * the legacy Layout.tsx renderAccordion(). Visibility predicates route through the
 * Approval Access permission model (util/adminPermissions.can) per spec §3.3 — the
 * same gates as the v1 Layout.tsx so both shells stay in lockstep. Super-admin-only
 * surfaces (Admin Employee View, PW, Union Wages, Sync, Selfie, Time Off Config)
 * remain gated on `admin`. Accordion category children flatten into their group.
 *
 * labelKey is the translator key Layout uses for that item; when omitted the item
 * falls back to translate(page) — exactly what Layout does for accordion children.
 * `label` is a literal (used for "Timesheet Comments", which Layout hard-codes).
 */

export interface NavCtx {
  /** Null-safe: a missing role (employee-only login, TAS-667) shows nothing. */
  userRole: AdminEmployeeRole | null | undefined;
  config: SiteConfig;
  miscTaskApprovalConfigs: MiscTaskApprovalConfig[];
  /** Employee Time Portal (P-TAS-20): 'employee' shows ONLY the My Time group; 'admin'
   *  (the default) shows every admin group and never the My Time group. */
  experience?: 'admin' | 'employee';
}

export interface NavItem {
  page: Page;
  icon: LucideIcon;
  labelKey?: string;
  label?: string;
  visible: (ctx: NavCtx) => boolean;
  /** marker: badge value supplied by the rendering component (pendingTimeOffCount, or the
   *  portal's badges: myTimesheet = last week not submitted (amber), myTimeOff = pending requests). */
  badge?: 'pendingTimeOff' | 'myTimesheet' | 'myTimeOff';
}

export interface NavGroup {
  label: string;
  items: NavItem[];
  /** Shown only in this experience (absent = admin). */
  experience?: 'admin' | 'employee';
}

/** The Employee Time Portal's sidebar (handoff §Shell): employee experience only. */
export const MY_TIME_GROUP: NavGroup = {
  label: 'My Time',
  experience: 'employee',
  items: [
    { page: Page.MyToday, icon: Clock, label: 'Today', visible: () => true },
    { page: Page.MyTimesheet, icon: ClipboardCheck, label: 'My Timesheet', visible: () => true, badge: 'myTimesheet' },
    { page: Page.MyTimeOff, icon: CalendarCheck, label: 'Time Off', visible: () => true, badge: 'myTimeOff' },
    { page: Page.MyActivity, icon: History, label: 'My Activity', visible: () => true },
  ],
};

/** Legacy drill-down pages that keep the Home nav item active (Layout.tsx:129-135). */
export const HOME_ALIAS_PAGES: Page[] = [
  Page.Dashboard,
  Page.EmployeeList,
  Page.SupervisorList,
  Page.TimeSheetPage,
];

export const NAV_GROUPS: NavGroup[] = [
  MY_TIME_GROUP,
  {
    label: 'Overview',
    items: [
      {
        page: Page.Dashboard,
        icon: Home,
        labelKey: 'Home',
        visible: ({ userRole: r }) => !!r?.admin || !!r?.payroll || !!r?.supervisor || !!r?.hr,
      },
      // Legacy v1 Home (Dashboard), kept accessible under the v2 shell as a fallback while
      // HomeV2 was being compared against it. Hidden everywhere v2 is on — which is every
      // shell that renders this menu — now that HomeV2 stands on its own. Commented out
      // rather than deleted so it can come back if a comparison is needed again; the
      // Page.DashboardOld route in App.tsx is untouched, so a direct link still loads it.
      // To restore, uncomment and add `History` back to the lucide-react import (it was
      // dropped because CRA fails the build on an unused import when CI=true).
      // {
      //   page: Page.DashboardOld,
      //   icon: History,
      //   label: 'Home (Old)',
      //   visible: ({ userRole: r }) => !!r?.admin || !!r?.payroll || !!r?.supervisor || !!r?.hr,
      // },
    ],
  },
  {
    label: 'Timesheets',
    items: [
      {
        page: Page.TimesheetReview,
        icon: ClipboardCheck,
        labelKey: 'timesheetReview',
        visible: ({ userRole: r, config }) =>
          !!config.showTimesheetReviewPage && can(r, 'viewTimesheets'),
      },
      {
        page: Page.TimeOffRequestApproval,
        icon: CalendarCheck,
        labelKey: 'timeOffRequestApproval',
        visible: ({ userRole: r, config }) =>
          can(r, 'viewTimeOffRequests') && !!config.showTimeOffApprovalPage,
        badge: 'pendingTimeOff',
      },
      {
        page: Page.GpsExceptions,
        icon: MapPin,
        label: 'GPS Exceptions',
        visible: ({ userRole: r, config }) =>
          can(r, 'viewGpsExceptions') && !!config.showGpsExceptions,
      },
      {
        page: Page.ProjectWork,
        icon: Briefcase,
        labelKey: 'projectWork',
        visible: ({ userRole: r, config }) => can(r, 'viewProjectWork') && !!config.showProjectWorkPage,
      },
      {
        page: Page.CrewWork,
        icon: Users,
        labelKey: 'crewWork',
        visible: ({ userRole: r, config }) => can(r, 'viewProjectWork') && !!config.showCrewWorkPage,
      },
      {
        page: Page.MiscellaneousTasks,
        icon: ListChecks,
        labelKey: 'MiscellaneousTasks',
        // ViewMiscTasks. The pure-supervisor exclusion is LEGACY-PAGE ONLY: the v1
        // MiscellaneousTasksPage renders every red in the period and filters per
        // supervisor client-side (MiscellaneousTasksPage.tsx:124-134), so letting a
        // plain supervisor in showed them everyone's reds — hence "needs configs".
        //
        // Under two-level approval that reasoning inverts. Routed supervisors ARE the
        // intended users (they hold the level-1 and level-2 slots), and the v2 queue
        // scopes server-side in MiscTaskQueueUtil.buildModel via the same
        // decideMiscTaskAction the approve endpoint calls — so there is nothing left
        // to leak. Gated on the same flag App.tsx uses to choose the page, so the
        // legacy rule still holds wherever the legacy page is what renders.
        visible: ({ userRole: r, config, miscTaskApprovalConfigs }) =>
          can(r, 'viewMiscTasks') && !!config.showMiscTasksPage &&
          (!!config.miscTaskTwoLevelApproval ||
            !!r?.admin || !!r?.payroll || !r?.supervisor ||
            (!!miscTaskApprovalConfigs && miscTaskApprovalConfigs.length > 0)),
      },
      {
        page: Page.Comments,
        icon: MessageSquare,
        label: 'Timesheet Comments',
        visible: ({ userRole: r, config }) =>
          can(r, 'viewTimesheetComments') && !!config.showCommentsPage,
      },
      {
        page: Page.TimesheetExceptions,
        icon: ShieldAlert,
        label: 'Timesheet Exceptions',
        visible: ({ userRole: r, config }) =>
          can(r, 'viewTimesheetComments') && !!config.showTimesheetExceptionsPage,
      },
      {
        // WCDP payroll: whose sick rate to calculate this week. Same gate as the
        // App.tsx route guard.
        page: Page.SickHours,
        icon: Thermometer,
        label: 'Sick Hours',
        visible: ({ userRole: r, config }) =>
          can(r, 'payrollApproveTimesheets') && !!config.showSickHoursPage,
      },
    ],
  },
  {
    label: 'People',
    items: [
      {
        page: Page.EmployeeView,
        icon: Users,
        labelKey: 'EmployeeView',
        visible: ({ userRole: r }) => can(r, 'viewEmployees'),
      },
      {
        // Super-admin only (D16). Replaces the read-only "Admin Employee View" stub
        // (WP9). Renders only for super admins; the route also redirects deep-links
        // from non-super-admins (App.tsx guard).
        page: Page.AdminManagement,
        icon: Shield,
        labelKey: 'AdminManagement',
        visible: ({ userRole: r }) => !!r?.admin,
      },
      {
        page: Page.SelfieVerificationExceptions,
        icon: Camera,
        labelKey: 'selfieVerificationExceptions',
        visible: ({ userRole: r, config }) =>
          can(r, 'viewSelfieExceptions') && !!config.showPhotoPages,
      },
      {
        // NPM payroll: who worked a Los Angeles job this year and whether Vista has them
        // on CA LA SICK. Same gate as the App.tsx route guard.
        page: Page.LaSick,
        icon: MapPinCheck,
        label: 'LA Sick',
        visible: ({ userRole: r, config }) => can(r, 'viewLASick') && !!config.showLaSickPage,
      },
    ],
  },
  {
    label: 'Setup',
    items: [
      {
        // Employee Roles (TAS-668): field-employee roles (apps + time entry). Replaces the
        // People › "Employee App Roles" item. Read-only for Edit employee role holders.
        page: Page.RolePage,
        icon: IdCard,
        label: 'Employee Roles',
        visible: ({ userRole: r }) => can(r, 'manageEmployeeRoles') || can(r, 'editEmployeeRole'),
      },
      {
        page: Page.AccountCode,
        icon: Hash,
        labelKey: 'AccountCodePage',
        visible: ({ userRole: r }) => can(r, 'viewAccountCodes'),
      },
      {
        page: Page.PayPeriods,
        icon: Calendar,
        labelKey: 'PayPeriods',
        visible: ({ userRole: r }) => can(r, 'managePayPeriods'),
      },
      {
        page: Page.Locations,
        icon: MapPin,
        labelKey: 'Locations',
        visible: ({ userRole: r }) => can(r, 'manageLocations'),
      },
      {
        page: Page.KioskDevices,
        icon: Tablet,
        label: 'Kiosk Devices',
        visible: ({ userRole: r, config }) => can(r, 'manageKioskDevices') && !!config.showKioskDevicesPage,
      },
      {
        page: Page.Projects,
        icon: Building2,
        labelKey: 'Projects',
        visible: ({ userRole: r, config }) => !!(config.showProjectsPage && r?.viewProjectsPage),
      },
      {
        page: Page.ProjectMap,
        icon: Map,
        labelKey: 'ProjectMap',
        visible: ({ userRole: r }) => !!r?.viewAllProjectsMap,
      },
      {
        page: Page.TimeOffConfiguration,
        icon: Settings,
        labelKey: 'timeOffConfiguration',
        visible: ({ userRole: r, config }) => !!r?.admin && !!config.showTimeOffConfig,
      },
      {
        page: Page.ReportsConfiguration,
        icon: Mail,
        label: 'Report Configuration',
        visible: ({ userRole: r, config }) => !!r?.admin && !!config.showReportsConfigPage,
      },
      {
        page: Page.SafetyMessage,
        icon: ShieldCheck,
        label: 'Safety Message',
        visible: ({ userRole: r, config }) => can(r, 'manageSafetyMessages') && !!config.showSafetyMessagePage,
      },
    ],
  },
  {
    label: 'Imports',
    items: [
      // All import pages gate on ImportTasks (D15 — consolidates the legacy
      // ImportPieces / ViewTaskImportPage flags), plus their per-tenant config flag.
      {
        page: Page.PieceImport,
        icon: FileInput,
        labelKey: 'taskImport',
        visible: ({ userRole: r, config }) => can(r, 'importTasks') && !!config.allow2020PieceImport,
      },
      {
        page: Page.RiceServicesPieceImport,
        icon: Upload,
        labelKey: 'riceServicesPieceImport',
        visible: ({ userRole: r, config }) => can(r, 'importTasks') && !!config.allowRiceServicesPieceImport,
      },
      {
        page: Page.UPFPieceBudgetImport,
        icon: FileInput,
        labelKey: 'upfPieceBudgetImport',
        visible: ({ userRole: r, config }) => can(r, 'importTasks') && !!config.allowUpfPieceImport,
      },
      // WCDP import pages — legacy accordion category "ImportExport". labels via translate(page).
      {
        page: Page.WCDPPaintSingleFamilyImport,
        icon: Upload,
        visible: ({ userRole: r, config }) => !!config.allowWCDPProjectImport && can(r, 'importTasks'),
      },
      {
        page: Page.WCDPPaintMultiFamilyImport,
        icon: Upload,
        visible: ({ userRole: r, config }) => !!config.allowWCDPProjectImport && can(r, 'importTasks'),
      },
      {
        page: Page.WCDPPaintMultiFamilyImportV1,
        icon: Upload,
        visible: ({ userRole: r, config }) => !!config.allowWCDPProjectImport && can(r, 'importTasks'),
      },
      {
        page: Page.WCDPDrywallSingleFamilyImport,
        icon: Upload,
        visible: ({ userRole: r, config }) => !!config.allowWCDPProjectImport && can(r, 'importTasks'),
      },
      {
        page: Page.WCDPDrywallMultiFamilyImport,
        icon: Upload,
        visible: ({ userRole: r, config }) => !!config.allowWCDPProjectImport && can(r, 'importTasks'),
      },
    ],
  },
  {
    label: 'Prevailing Wage',
    items: [
      // Legacy accordion category "PrevailingWage"
      // (shouldShowCategory: showPrevailingWagePages && admin). labels via translate(page).
      {
        page: Page.PWClassifications,
        icon: Layers,
        visible: ({ userRole: r, config }) => !!config.showPrevailingWagePages && !!r?.admin,
      },
      {
        page: Page.PWFringeTypes,
        icon: Coins,
        visible: ({ userRole: r, config }) => !!config.showPrevailingWagePages && !!r?.admin,
      },
      {
        page: Page.PWProjectSetup,
        icon: SlidersHorizontal,
        visible: ({ userRole: r, config }) => !!config.showPrevailingWagePages && !!r?.admin,
      },
      {
        page: Page.UnionWages,
        icon: Scale,
        labelKey: 'unionWages',
        visible: ({ userRole: r, config }) => !!r?.admin && !!config.showUnionClassificationPages,
      },
    ],
  },
  {
    label: 'Field Ops',
    items: [
      {
        page: Page.WorkOrders,
        icon: Package,
        label: 'Work Orders',
        visible: ({ userRole: r, config }) => canViewWorkOrders(r) && !!config.showWorkOrdersPage,
      },
      {
        page: Page.ChangeOrders,
        icon: FileInput,
        label: 'Change Orders',
        visible: ({ userRole: r, config }) => canViewWorkOrders(r) && !!config.showChangeOrdersPage,
      },
      {
        page: Page.CustomerContacts,
        icon: BookUser,
        label: 'Builder Contacts',
        visible: ({ userRole: r, config }) => canViewWorkOrders(r) && !!config.showWorkOrdersPage,
      },
      {
        page: Page.ModifiedDuty,
        icon: Stethoscope,
        labelKey: 'ModifiedDuty',
        visible: ({ userRole: r, config }) => can(r, 'viewModifiedDuty') && !!config.showModifiedDuty,
      },
      {
        page: Page.Reports,
        icon: BarChart3,
        labelKey: 'Reports',
        visible: ({ userRole: r, config }) => can(r, 'viewReports') && !!config.showReportsPage,
      },
    ],
  },
  {
    // Split out of Field Ops so a tenant whose only entry here is a report does
    // not read as "Field Ops". Groups with no visible items are dropped by
    // getVisibleGroups, so NPM (which has none of the Field Ops entries above)
    // simply sees Reports, and WCDP keeps Field Ops for its work/change orders.
    label: 'Reports',
    items: [
      // The v2 Reports hub replaces the four entries below with one. Those four
      // stay registered (and stay in the v1 Layout.tsx nav) until NPM is
      // confirmed on the v2 shell — the two nav files are independent, so
      // deleting the pages before then would strand a v1 tenant with no reports.
      {
        page: Page.ReportsHub,
        icon: FileBarChart2,
        label: 'Reports',
        visible: ({ userRole: r, config }) => !!config.showReportsHubPage && can(r, 'viewReports'),
      },
      // Legacy accordion category "Reports" → ViewReports. labels via translate(page).
      {
        page: Page.AuditReport,
        icon: FileText,
        visible: ({ userRole: r, config }) =>
          !config.showReportsHubPage && !!config.showAuditReportPage && can(r, 'viewReports'),
      },
      {
        page: Page.AttendanceReport,
        icon: FileText,
        visible: ({ userRole: r, config }) =>
          !config.showReportsHubPage && !!config.showAuditReportPage && can(r, 'viewReports'),
      },
      {
        page: Page.TimesheetDeadlineReport,
        icon: FileText,
        visible: ({ userRole: r, config }) =>
          !config.showReportsHubPage && !!config.showAuditReportPage && can(r, 'viewReports') && !!config.showRiceServicesReports,
      },
      {
        page: Page.WeeklyOpsReport,
        icon: FileText,
        visible: ({ userRole: r, config }) =>
          !config.showReportsHubPage && !!config.showAuditReportPage && can(r, 'viewReports') && !!config.showRiceServicesReports,
      },
    ],
  },
  {
    label: 'Payroll & Sync',
    items: [
      // Paycor / V1 / V2 batch exports → ExportPayroll (D15). labels via translate(page).
      {
        page: Page.PaycorDownload,
        icon: Download,
        visible: ({ userRole: r, config }) => !!config.allowV1Export && can(r, 'exportPayroll'),
      },
      {
        page: Page.ManualPaycor,
        icon: Download,
        visible: ({ userRole: r, config }) => !!config.allowV1Export && can(r, 'exportPayroll'),
      },
      {
        page: Page.PaycorDownloadV2,
        icon: Download,
        // Replaced by the board's "Generate Paycor File" when showWcdpBatchExport is on (TAS-682).
        visible: ({ userRole: r, config }) =>
          !!config.allowV1Export && !config.showWcdpBatchExport && can(r, 'exportPayroll'),
      },
      {
        page: Page.PaycorPrdaLocal,
        icon: Download,
        // Own flag (absent = old allowV1Export gate) so WCDP can hide every V1 page.
        visible: ({ userRole: r, config }) =>
          !!(config.showPrdaLocal ?? config.allowV1Export) && can(r, 'exportPayroll'),
      },
      {
        page: Page.PrdaMonthlyReports,
        icon: FileArchive,
        visible: ({ userRole: r, config }) => !!config.showPrdaMonthlyReportsPage && can(r, 'exportPayroll'),
      },
      {
        page: Page.V1Supervisors,
        icon: Package,
        visible: ({ userRole: r, config }) => !!config.allowV1Export && can(r, 'exportPayroll'),
      },
      {
        page: Page.V1BatchExport,
        icon: Package,
        visible: ({ userRole: r, config }) => !!config.allowV1Export && can(r, 'exportPayroll'),
      },
      {
        page: Page.V2BatchExport,
        icon: Package,
        // Replaced by the board's per-crew IAS Export when showWcdpBatchExport is on (TAS-682).
        visible: ({ userRole: r, config }) =>
          !!config.allowV2Export && !config.showWcdpBatchExport && can(r, 'exportPayroll'),
      },
      // Vista export → ExportAccounting OR ExportPayroll (tagged per customer, D15).
      {
        page: Page.VistaExport,
        icon: Package,
        visible: ({ userRole: r, config }) =>
          !!config.allowVistaExport && (can(r, 'exportAccounting') || can(r, 'exportPayroll')),
      },
      {
        page: Page.ProcessPayroll,
        icon: Wallet,
        labelKey: 'processPayroll',
        visible: ({ userRole: r, config }) => can(r, 'processPayroll') && !!config.showProcessPayroll,
      },
      {
        page: Page.SyncStatus,
        icon: RefreshCw,
        labelKey: 'syncStatus',
        visible: ({ userRole: r, config }) => !!r?.admin && !!config.showSyncStatusPage,
      },
    ],
  },
];

/** Groups (and their items) that are visible for the given context; empty groups dropped. */
export function getVisibleGroups(ctx: NavCtx): NavGroup[] {
  const experience = ctx.experience ?? 'admin';
  return NAV_GROUPS
    .filter((g) => (g.experience ?? 'admin') === experience)
    .map((g) => ({ label: g.label, items: g.items.filter((it) => it.visible(ctx)) }))
    .filter((g) => g.items.length > 0);
}

/**
 * Pages driven by their own date control rather than the shell's week, so the global
 * header week pill is hidden there (a week control that does nothing would mislead).
 * LA Sick has its own Year stepper.
 */
export const PAGES_WITHOUT_WEEK_PILL: ReadonlySet<Page> = new Set<Page>([Page.LaSick]);

/**
 * Locate the group + item for a page (used for the breadcrumb). Legacy drill-down
 * pages (EmployeeList/SupervisorList/TimeSheetPage) resolve to the Home entry,
 * matching Layout.tsx active logic. Ignores visibility so the crumb always resolves.
 */
export function findNavEntry(page: Page): { group: string; item: NavItem } | null {
  const target = HOME_ALIAS_PAGES.includes(page) ? Page.Dashboard : page;
  for (const g of NAV_GROUPS) {
    const item = g.items.find((it) => it.page === target);
    if (item) return { group: g.label, item };
  }
  return null;
}
